import { CONSTANTS__generation_settings } from '@shared/constants/CONSTANTS__generation_settings';
import { CONSTANTS_GENERATION__slider_step } from '@shared/constants/CONSTANTS_GENERATION__slider_step';
import { CONSTANTS_GENERATION__value_range as CONSTANTS } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__generation_scheduler } from '@shared/dto/generation/_enums/ENUM__generation_scheduler';
import { FIELD_PRIMITIVE__seed } from '@shared/dto/generation/graph_invoke/_fields/_primitives/FIELD_PRIMITIVE__seed';
import { ENUM__list_mode } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/ENUM__list_mode';
import { FIELD__list_control_net } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_control_net';
import { FIELD__list_ip_adapter } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_ip_adapter';
import { FIELD__list_t2i_adapter } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_t2i_adapter';
import { FIELD_PRIMITIVE__list_float_dynamic } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD_PRIMITIVE__list_float_dynamic';
import { FIELD_SD__list_conditioning_masked } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD_SD__list_conditioning_masked';
import { FIELD_PRIMITIVE__float } from '@shared/dto/generation/graph_invoke/_fields/_primitives/number/FIELD_PRIMITIVE__float';
import { FIELD_PRIMITIVE__int } from '@shared/dto/generation/graph_invoke/_fields/_primitives/number/FIELD_PRIMITIVE__int';
import { FIELD_PRIMITIVE__string_enum } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/FIELD_PRIMITIVE__string_enum';
import { FIELD__tensor_latents } from '@shared/dto/generation/graph_invoke/_fields/_tensor/FIELD__tensor_latents';
import { FIELD_SD__conditioning } from '@shared/dto/generation/graph_invoke/_fields/guidance/FIELD_SD__conditioning';
import { FIELD__ml_asset_unet } from '@shared/dto/generation/graph_invoke/_fields/ml_asset/FIELD__ml_asset_unet';
import { DTO_GRAPH__edge_autoconnect_info_list } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__edge_autoconnect_info_list';
import { DTO_GRAPH__instance } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__instance';
import { ENUM__invocation_type } from '@shared/dto/generation/graph_invoke/_invocations/_base/ENUM__invocation_type';
import { INVOCATION_LATENTS__create_noise } from '@shared/dto/generation/graph_invoke/_invocations/latents/INVOCATION_LATENTS__create_noise';
import { INVOCATION_SD__control_net } from '@shared/dto/generation/graph_invoke/_invocations/sd/guidance/INVOCATION_SD__control_net';
import { INVOCATION_SD__text_encoder } from '@shared/dto/generation/graph_invoke/_invocations/sd/guidance/INVOCATION_SD__text_encoder';
import { INVOCATION_SD__text_encoder_masked } from '@shared/dto/generation/graph_invoke/_invocations/sd/guidance/INVOCATION_SD__text_encoder_masked';
import { INVOCATION_SD__base } from '@shared/dto/generation/graph_invoke/_invocations/sd/INVOCATION_SD__base';
import { INVOCATION_SD__image_to_latents } from '@shared/dto/generation/graph_invoke/_invocations/sd/vae/INVOCATION_SD__image_to_latents';
import { INVOCATION_SD__latents_to_image } from '@shared/dto/generation/graph_invoke/_invocations/sd/vae/INVOCATION_SD__latents_to_image';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';
import { FIELD__base } from '../../../_fields/_base/FIELD__base';
import { FIELD__tensor_image } from '../../../_fields/_tensor/FIELD__tensor_image';
import { INVOCATION_SD__ip_adapter } from '../guidance/INVOCATION_SD__ip_adapter';
import { INVOCATION_SD__lora_select } from '../guidance/INVOCATION_SD__lora_select';
import { INVOCATION_SD__t2i_adapter } from '../guidance/INVOCATION_SD__t2i_adapter';

export class INVOCATION_SD__denoise_latents_base extends INVOCATION_SD__base {
  /*
   * STATIC METHODS
   */

  /*
   * FIELDS TEXT GLOBAL
   */
  @FIELD_SD__conditioning.init_as_input({})
  conditioning_global__positive!: FIELD_SD__conditioning;

  @FIELD_SD__conditioning.init_as_input({})
  conditioning_global__negative!: FIELD_SD__conditioning;

  /*
   * FIELDS TEXT REGIONAL
   */

  @FIELD_SD__list_conditioning_masked.init_as_input({}, { data__is_required: false })
  conditioning_regional__positive!: FIELD_SD__list_conditioning_masked;

  @FIELD_SD__list_conditioning_masked.init_as_input({}, { data__is_required: false })
  conditioning_regional__negative!: FIELD_SD__list_conditioning_masked;

  // // todo:remove and use combo field SD conditioning
  // @FIELD__list_tensor_image_mask_boolean.init_as_input({  items_amount_can_be_changed_manually: false })
  // conditioning_negative_mask!: FIELD__list_tensor_image_mask_boolean;

  @FIELD_PRIMITIVE__list_float_dynamic.init_as_input(
    {
      value: [7.5],
      mode: ENUM__list_mode.ONE_ITEM,
      value_range: CONSTANTS.GUIDANCE_SCALE,
      values_allowed: CONSTANTS.GUIDANCE_SCALE,
    },
    {
      field_role: '🎚️ CFG Scale',
    },
  )
  cfg_scale!: FIELD_PRIMITIVE__list_float_dynamic;

  @FIELD_PRIMITIVE__float.init_as_input(
    {
      value: 0,
      value_range: CONSTANTS.ZERO_TO_ONE,
      values_allowed: CONSTANTS.ZERO_TO_ONE,
    },
    { field_role: '🎚️ CFG Rescale' },
  )
  cfg_rescale_multiplier!: FIELD_PRIMITIVE__float;

  /*
   * FIELDS IMAGE
   */
  @FIELD__tensor_latents.init_as_input({}, { field_role: '🔮 Noise', data__is_required: false })
  noise!: FIELD__tensor_latents;

  @FIELD_PRIMITIVE__seed.init_as_input(
    {
      value: 0,
      value_range: CONSTANTS__generation_settings.SEEDS_DEFAULT,
      values_allowed: CONSTANTS__generation_settings.SEED_RANGE,
    },
    { data__is_required: false },
  )
  seed!: FIELD_PRIMITIVE__seed;

  @FIELD__tensor_latents.init_as_input({}, { field_role: '🔮 Latents', data__is_required: false })
  latents!: FIELD__tensor_latents;

  @FIELD__tensor_latents.init_as_input({}, { field_role: '🔮 Latents Under Mask', data__is_required: false })
  latents_under_mask!: FIELD__tensor_latents;

  @FIELD__tensor_image.init_as_input({}, { field_role: '🔢 Mask', data__is_required: false })
  tensor_image__mask!: FIELD__tensor_image;

  /*
   * FIELDS DENOISING
   */

  @FIELD_PRIMITIVE__int.init_as_input({
    // value: 2,
    // todo:uncomment
    value: 10,
    value_range: CONSTANTS.INFERENCE_STEPS,
    values_allowed: CONSTANTS.INFERENCE_STEPS,
    slider_step_values: CONSTANTS_GENERATION__slider_step.INFERENCE_STEPS_VALUES,
  })
  steps!: FIELD_PRIMITIVE__int;

  @FIELD_PRIMITIVE__string_enum.register_input__enum(ENUM__generation_scheduler.EULER, ENUM__generation_scheduler)
  scheduler!: FIELD_PRIMITIVE__string_enum;

  @FIELD_PRIMITIVE__float.init_as_input(
    {
      value: 1.0,
      value_range: new DTO__number_range(0.6, 1.0),
      values_allowed: CONSTANTS.ZERO_TO_ONE,

      slider_step: 0.1,
      slider_step_values: CONSTANTS_GENERATION__slider_step.DENOISE_STRENGTH_VALUES,
    },
    { field_role: '🎚️ Creativity' },
  )
  denoising_strength!: FIELD_PRIMITIVE__float;

  @FIELD_PRIMITIVE__float.init_as_input(CONSTANTS__generation_settings.PARAMS_STEPS_BEGIN, { field_role: '🏁 Denoising Start' })
  denoising_start!: FIELD_PRIMITIVE__float;

  @FIELD_PRIMITIVE__float.init_as_input(CONSTANTS__generation_settings.PARAMS_STEPS_END, { field_role: '🏁 Denoising End' })
  denoising_end!: FIELD_PRIMITIVE__float;

  /*
   * FIELDS ML
   */

  @FIELD__ml_asset_unet.init_as_input({}, {})
  unet!: FIELD__ml_asset_unet;

  @FIELD__list_control_net.init_as_input(
    { item_meta: MAP__serializable.from_object({ ui__has_component: false }) },
    { ui__has_component: false, data__is_required: false },
  )
  control!: FIELD__list_control_net;

  @FIELD__list_ip_adapter.init_as_input(
    { item_meta: MAP__serializable.from_object({ ui__has_component: false }) },
    { ui__has_component: false, data__is_required: false },
  )
  ip_adapter!: FIELD__list_ip_adapter;

  @FIELD__list_t2i_adapter.init_as_input(
    { item_meta: MAP__serializable.from_object({ ui__has_component: false }) },
    { ui__has_component: false, data__is_required: false },
  )
  t2i_adapter!: FIELD__list_t2i_adapter;

  /*
   * FIELDS OUT
   */

  @FIELD__tensor_latents.init_as_output({}, { field_role: '🔮 Generated Latents' })
  out__latents!: FIELD__tensor_latents;

  /*
   * EVENTS
   */

  edges__try_autocreate(graph: DTO_GRAPH__instance) {
    super.edges__try_autocreate(graph);

    /*
     * INVOCATION_SD__denoise_latents_base -> INVOCATION_SD__latents_to_image
     */

    this.edges__try_autocreate_from_left_to_right_one_field(graph, INVOCATION_SD__latents_to_image, ['latents'], this.out__latents);

    /*
     * INVOCATION__noise -> INVOCATION_SD__denoise_latents_base
     */

    let fields_from_noise = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_LATENTS__create_noise>();
    fields_from_noise.invocation_type = INVOCATION_LATENTS__create_noise;
    fields_from_noise.add(this.seed, ['seed']);
    fields_from_noise.add(this.noise, ['out__latents']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_noise);

    /*
     * INVOCATION_SD__text_encoder -> INVOCATION_SD__denoise_latents_base
     */

    let fields_from_text_encoder = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__text_encoder>();
    fields_from_text_encoder.invocation_type = INVOCATION_SD__text_encoder;
    fields_from_text_encoder.add(this.conditioning_global__positive, ['out__positive']);
    fields_from_text_encoder.add(this.conditioning_global__negative, ['out__negative']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_text_encoder);

    /*
     * INVOCATION_SD__text_encoder_masked -> INVOCATION_SD__denoise_latents_base -> INVOCATION__noise
     */

    let fields_from_text_encoder_masked = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__text_encoder_masked>();
    fields_from_text_encoder_masked.invocation_type = INVOCATION_SD__text_encoder_masked;
    fields_from_text_encoder_masked.add(this.conditioning_regional__positive, ['out__positive']);
    fields_from_text_encoder_masked.add(this.conditioning_regional__negative, ['out__negative']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_text_encoder_masked);

    /*
     * INVOCATION_SD__image_to_latents -> INVOCATION_SD__denoise_latents_base
     */

    let fields_from_image_to_latents = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__image_to_latents>();
    fields_from_image_to_latents.invocation_type = INVOCATION_SD__image_to_latents;
    fields_from_image_to_latents.add(this.latents, ['out__tensor_latents']);
    fields_from_image_to_latents.add(this.latents_under_mask, ['out__tensor_latents__under_mask']);
    fields_from_image_to_latents.add(this.tensor_image__mask, ['out__tensor_image__mask']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_to_latents);

    /*
     * INVOCATION_SD__control_net -> INVOCATION_SD__denoise_latents_base
     */
    let fields_from_image_from_control_net = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__control_net>();
    fields_from_image_from_control_net.invocation_type = INVOCATION_SD__control_net;
    fields_from_image_from_control_net.add(this.control, ['control_net']);
    fields_from_image_from_control_net.create_only_one_connection = false;

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_from_control_net);

    /*
     * fields_from_image_from_ip_adapter -> INVOCATION_SD__denoise_latents_base
     */
    let fields_from_image_from_ip_adapter = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__ip_adapter>();
    fields_from_image_from_ip_adapter.invocation_type = INVOCATION_SD__ip_adapter;
    fields_from_image_from_ip_adapter.add(this.ip_adapter, ['ip_adapter']);
    fields_from_image_from_ip_adapter.create_only_one_connection = false;

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_from_ip_adapter);

    /*
     * INVOCATION_SD__t2i_adapter -> INVOCATION_SD__denoise_latents_base
     */
    let fields_from_image_from_t2i_adapter = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__t2i_adapter>();
    fields_from_image_from_t2i_adapter.invocation_type = INVOCATION_SD__t2i_adapter;
    fields_from_image_from_t2i_adapter.add(this.t2i_adapter, ['t2i_adapter']);
    fields_from_image_from_t2i_adapter.create_only_one_connection = false;

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_from_t2i_adapter);

    /*
     * INVOCATION_SD__lora_select -> INVOCATION_SD__denoise_latents_base
     */
    let fields_from_image_from_lora_select = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__lora_select>();
    fields_from_image_from_lora_select.invocation_type = INVOCATION_SD__lora_select;
    fields_from_image_from_lora_select.add(this.unet.loras, ['loras']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_from_ip_adapter);
  }

  /*
   * METHODS
   */

  constructor(type: ENUM__invocation_type) {
    super(type);
  }

  post_init() {
    super.post_init();

    this.scheduler.meta.field_role = '📈 Scheduler';

    this.steps.meta.field_role = '🪜 Steps';
  }

  prepare_for_usage_in(graph: DTO_GRAPH__instance) {
    super.prepare_for_usage_in(graph);

    this.denoising_start.sync_with_field_max(this.denoising_end);

    this.field_list__clear_without_edges(graph, this.conditioning_regional__positive);
    this.field_list__clear_without_edges(graph, this.conditioning_regional__negative);
  }

  fields_ids__get_wanted_to_be_filled() {
    let out = super.fields_ids__get_wanted_to_be_filled();

    let out_fields: FIELD__base[] = [this.conditioning_global__negative, this.conditioning_global__positive];

    if (!this.noise.meta.data__is_linked && !this.latents.meta.data__is_linked) {
      out_fields.push(this.latents);
      out_fields.push(this.noise);
    }

    out = [...out, ...out_fields.map(field => field.meta.field_id)];

    return out;
  }

  /*
   * VALIDATION
   */

  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    let noise = this.noise.find_source(graph);
    let latents = this.latents.find_source(graph);

    if (!noise.has_effect_on_generation_runtime(graph) && !latents.has_effect_on_generation_runtime(graph)) return false;

    return super.has_effect_on_generation_runtime(graph);
  }
}
